@if ({ timeZone: timeZone$ | async }; as data) {

<div class="tile">
  <!--
    branding is a single layer on the tile's trailing edge - a wedge in the
    bottom-right corner, or a narrow ribbon down the full height - so the card
    itself is never tinted
  -->
  @if (isBrandedWithSpecialDesign || isBrandedWithRibbon) {
  <span
    class="tile__branding"
    [class.tile__branding--wedge]="isBrandedWithSpecialDesign"
    [class.tile__branding--ribbon]="isBrandedWithRibbon"
    [style.color]="announcement.announcementBrandingListColor"
  ></span>
  }

  <div class="tile__content">
    <div class="tile__main">
      <div class="tile__heading">
        <a
          class="tile__title"
          [class.tile__title--flame]="isFramedWithFlame"
          [class.tile__title--plus]="isFramedWithPlus"
          [routerLink]="[
            '/announcement',
            announcement.announcementId,
            announcement.urlFriendlyTitle
          ]"
        >
          @if (titleDecorationSrc) {
          <svg-icon
            class="tile__decoration"
            [svgClass]="'ico-decoration'"
            [src]="titleDecorationSrc"
          ></svg-icon>
          }
          <span class="tile__title-text">
            <span class="tile__title-underline">{{ announcement.title }}</span>
          </span>
        </a>

        <!-- salary, contact and apply hints, in the order the frames show them -->
        <div class="tile__flags">
          <!--
            the frame types the lari sign as text; it is drawn from the asset
            instead so it cannot fall back to a tofu box in a font without
            U+20BE
          -->
          @if (announcement.isSalaryAvailable) {
          <svg-icon
            class="tile__flag"
            [svgClass]="'ico-currency'"
            src="assets/icons/currency-icon-{{providerWebsiteId}}.svg"
          ></svg-icon>
          }

          @if (announcement.isContactPhoneAvailable) {
          <svg-icon
            class="tile__flag"
            [svgClass]="'ico-phone'"
            src="assets/icons/phone.svg"
          ></svg-icon>
          }

          @if (announcement?.isWorkFromHome) {
          <svg-icon
            class="tile__flag"
            [svgClass]="'ico-home'"
            [title]="'label.WorkFromHome' | translate"
            src="assets/icons/home-dynamic.svg"
          ></svg-icon>
          }

          @if (isAppliedViaWebsite) {
          <svg-icon
            class="tile__flag"
            [svgClass]="'ico-flash'"
            [matTooltip]="'text.EasyApplyTooltip' | translate"
            src="assets/icons/send-cv-ats.svg"
          ></svg-icon>
          }

          @if (isAppliedViaExternalUrl) {
          <svg-icon
            class="tile__flag"
            [svgClass]="'ico-external'"
            [matTooltip]="'text.ExternalApplyTooltip' | translate"
            src="assets/icons/send-cv-external.svg"
          ></svg-icon>
          }

          @if (isInvitedJobseeker) {
          <svg-icon
            class="tile__flag"
            [svgClass]="'ico-invited'"
            [matTooltip]="'label.YouAreInvitedToThisVacancy' | translate"
            src="assets/icons/invited.svg"
          ></svg-icon>
          }
        </div>
      </div>

      @if (announcement?.extendedLocations?.length) {
      <div class="tile__locations">
        @for (location of announcement.extendedLocations; track $index) {
        <span class="tile__location">
          <svg-icon
            class="tile__location-icon"
            [svgClass]="'ico-pin'"
            src="assets/icons/location-pin.svg"
          ></svg-icon>
          <span class="tile__location-name">{{ locationName(location) }}</span>
        </span>
        }
      </div>
      }

      <div class="tile__meta">
        <div class="tile__company">
          @if (announcement.isAnonymous) {
          <span class="tile__company-name">{{ announcement.customerName }}</span>
          } @else {
          <a
            class="tile__company-name"
            [routerLink]="[
              '/customer',
              announcement.customerId,
              announcement.urlFriendlyCustomerName
            ]"
            ><span class="tile__company-underline">{{ announcement.customerName }}</span></a
          >
          }

          @if (announcement.isVerified) {
          <img
            class="tile__company-badge"
            [title]="'label.Verified' | translate"
            src="assets/img/common/verification-icon.svg"
            alt=""
          />
          }

          @if (announcement.isWithCvBox) {
          <svg-icon
            class="tile__company-cv-box"
            [svgClass]="'ico-cv-box'"
            src="assets/icons/cv-box-company.svg"
          ></svg-icon>
          }
        </div>

        <div class="tile__dates">
          <span [class.tile__date--today]="announcement.isPublishedToday">{{
            announcement.isPublishedToday
              ? ('label.Today' | translate)
              : (announcement.renewalDate || announcement.publishDate
                | date: 'd MMM' : data.timeZone : currentLang)
          }}</span>
          <span>-</span>
          <span [class.tile__date--today]="announcement.isDeadLineToday">{{
            announcement.isDeadLineToday
              ? ('label.Today' | translate)
              : (announcement.deadlineDate
                | date: 'd MMM' : data.timeZone : currentLang)
          }}</span>
        </div>
      </div>
    </div>

    @if (announcement.isWithLogo) {
    <div class="tile__logo">
      @if (announcement.isAnonymous) {
      <img
        class="tile__logo-img"
        loading="lazy"
        [src]="announcement.logoFilename"
        alt=""
      />
      } @else {
      <a
        class="tile__logo-link"
        [routerLink]="[
          '/customer',
          announcement.customerId,
          announcement.urlFriendlyCustomerName
        ]"
      >
        <img
          class="tile__logo-img"
          loading="lazy"
          [src]="announcement.logoFilename"
          alt=""
        />
      </a>
      }
    </div>
    }

    <span class="tile__favorite">
      <svg-icon
        class="tile__heart"
        (click)="onToggleFavorite()"
        [svgClass]="
          announcement.isFavorite ? 'ico-heart' : 'ico-heart ico-heart--muted'
        "
        [src]="
          announcement.isFavorite
            ? 'assets/icons/fav-icon-filled-' + providerWebsiteId + '.svg'
            : 'assets/icons/fav-icon-' + providerWebsiteId + '.svg'
        "
        [matTooltip]="'label.AddToFavorites' | translate"
      ></svg-icon>
    </span>

    <span class="tile__section">{{
      announcementListSectionMap[announcement.listingSection] | translate
    }}</span>
  </div>
</div>

}
